import { BRAND_NAME } from "../../constants/brand";
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, IconButton, InputAdornment, Link, TextField, Typography, useMediaQuery } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import signinStyle from '../../Styles/ComponentStyles/Signin.module.scss';

import google from '../../assets/Images/Signin/Signin-Google.svg';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import { failedGoogleSignInNotice } from './googleSignInNotice';
import GoogleSignInNoticeBox from './GoogleSignInNoticeBox';

import GlobalTextfield from '../../GlobalComponent/GlobalTextfield/GlobalTextfield';
import newRBlogo from '../../assets/Images/mar-lockup-white.svg';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MovingIcon from '@mui/icons-material/Moving';
import EmailIcon from '@mui/icons-material/Email';
import HttpsIcon from '@mui/icons-material/Https';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const points = [
    {
        img: MovingIcon,
        text: "Income Gap Supplementation"
    },
    {
        img: CalculateOutlinedIcon,
        text: "Roth Conversion Analysis"
    },
    {
        img: ShieldOutlinedIcon,
        text: "Qualified Account Spend Down"
    }
];

const Signin = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const isLessThan1024 = useMediaQuery('(max-width:1024px)')
    const [searchParams, setSearchParams] = useSearchParams();
    // A Google sign-in the API could not complete comes back here as /login?status=invalid&reason=<why>[&email=..].
    // The sentence is kept in state and the query is dropped from the URL, so the email does not stay in the
    // address bar, the history or the access logs of a refresh.
    const [googleSignInNotice] = useState(() => failedGoogleSignInNotice(searchParams));
    useEffect(() => {
        if (searchParams.get('status') === 'invalid') {
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Context
    const {
        signinFormDetails,
        setSigninFormDetails,
        setUserDetail,
        setFlagLoader,
        SigninAPIFunction,
        signinWithGoogle,
        forgetPasswordAPI,
    } = useContext(UserContext) as UserContextPayload;

    // State
    const [showPassword, setShowPassword] = useState(false);
    const [touched, setTouched] = useState({
        email: false,
        password: false
    });
    const [errors, setErrors] = useState({
        email: '',
        password: ''
    });
    // const [currentLogo, setCurrentLogo] = useState("")
    const [flagForgotPwdClick, setFlagForgotPwdClick] = useState(false)
    const [forgetPwdEmail, setForgetPwdEmail] = useState("")
    const [forgotPwdError, setForgotPwdError] = useState("")
    const [apiError, setApiError] = useState('');

    const emailRegex = /^[a-z0-9.]+@[a-z0-9]+(\.[a-z]{2,})+$/;

    // Event handlers
    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        setSigninFormDetails({
            ...signinFormDetails,
            email: email
        });

        if (touched.email) {
            setErrors(prev => ({ ...prev, email: '' }));
        }
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const password = event.target.value;
        setSigninFormDetails({
            ...signinFormDetails,
            password: password
        });

        if (touched.password) {
            setErrors(prev => ({ ...prev, password: '' }));
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleForgetPassword = async () => {
        // Ensure the email field is not empty
        if (forgetPwdEmail.trim() === '') {
            setForgotPwdError("Email cannot be empty");
            return;
        }
        // Clear previous error if any
        setForgotPwdError("");

        const payload = {
            email: forgetPwdEmail
        };
        try {
            setFlagLoader(true);
            const response = await forgetPasswordAPI(payload);
            if (response?.status) {
                setFlagLoader(false);
                showSnackbar("Email sent successfully!", "success");
                setForgetPwdEmail("");
            } else {
                setFlagLoader(false);
                showSnackbar(response?.error?.message || "Error sending email", "error");
            }
        } catch (error) {
            setFlagLoader(false);
            console.log(error, "Error fetching data!");
        } finally {
            setFlagLoader(false);
        }
    };

    const handleSignIn = async () => {
        setApiError('');

        // Mark all fields as touched on submit
        setTouched({
            email: true,
            password: true
        });

        // Check for empty fields
        let emailError = '';
        let passwordError = '';

        if (signinFormDetails.email.trim() === '') {
            emailError = 'Email cannot be empty';
        }
        if (signinFormDetails.password.trim() === '') {
            passwordError = 'Password cannot be empty';
        }

        // Update errors based on empty checks
        setErrors({
            email: emailError,
            password: passwordError
        });

        // If there are any errors, prevent submission
        if (emailError || passwordError) {
            return;
        }

        // Proceed with the API call
        setFlagLoader(true);
        const payload = {
            email: signinFormDetails.email,
            password: signinFormDetails.password,
            remember_me_flag: signinFormDetails.remember_me_flag
        };

        const response = await SigninAPIFunction(payload);
        if (response?.status) {
            if (response?.data) {
                const userData = Array.isArray(response.data) ? response.data[0] : response.data;
                setUserDetail({
                    ...userData,
                    flagLoggedIn: true
                });
                setFlagLoader(false);
                navigate('/');
                // showSnackbar('Logged in Successfully!', 'success');
            }
        } else {
            const errorMessage = response?.error?.message || 'Something Went Wrong!';
            setFlagLoader(false);
            setApiError(errorMessage);
            // showSnackbar(errorMessage, 'error');
            navigate('/login');
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setFlagLoader(true);
            const response = await signinWithGoogle();
            if (response.url) {
                window.location.href = response.url;
            } else {
                const errorMessage = response.message || 'Unable to sign in with Google.';
                setFlagLoader(false)
                showSnackbar(errorMessage, 'error');
                navigate('/login');
            }
        } catch (err) {
            console.error("Error during login:", err);
            setFlagLoader(false);
            navigate('/login');
        } finally {
            setFlagLoader(false);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const renderMobileLayout = () => (
        <Grid item xs={12} className={signinStyle.smallScreenStyle} alignItems={"flex-start"}>
            <Grid container className={signinStyle.smallScreenRightMainContainer}>
                {/* {
                                        companyDetails?.company_full_logo &&
                                        <Grid item xs={12} className={signinStyle.logoContainer}>
                                            <img style={{ height: "70px" }} src={companyDetails?.company_full_logo} alt={BRAND_NAME} />
                                        </Grid>
                                    } */}
                <Box
                    component="img"
                    alt="Logo"
                    src={newRBlogo}
                />
                {flagForgotPwdClick ?
                    <>
                        <Grid item xs={12} className={signinStyle.welcomeContainer}>
                            <Grid container gap={4}>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography component={'span'} className={signinStyle.welcomeTextForSmallScreen}>
                                                Forgot Password?
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.subTextFwdPassForSmallScreen}>
                                                We've sent you a password reset link
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <GlobalTextfield
                                        // required
                                        variant="outlined"
                                        placeholder='Email Address'
                                        fullWidth
                                        stylingClassName={signinStyle.signinTextfield}
                                        value={forgetPwdEmail}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;
                                            setForgetPwdEmail(inputValue);

                                            if (inputValue && emailRegex.test(inputValue)) {
                                                setForgotPwdError("");
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {forgotPwdError && (
                                        <span style={{ color: '#810505', marginLeft: '7px', fontSize: "14px" }}>
                                            {forgotPwdError}
                                        </span>
                                    )}
                                    <Typography className={signinStyle.subTextFwdPassResetForSmallScreen}>
                                        Click the link in the email to reset your password. If you don't see the email, check your spam folder.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Button
                                                variant='contained'
                                                size='large'
                                                fullWidth
                                                className={signinStyle.signinBtn}
                                                onClick={() => handleForgetPassword()}
                                            >
                                                Get Reset Link
                                            </Button>
                                        </Grid>
                                        <Divider
                                            className={signinStyle.signindivider}
                                            style={{ width: '100%', margin: '40px 0px' }}
                                        >
                                            <span className={signinStyle.signinDividerOrLabel}>Or</span>
                                        </Divider>
                                        <Grid item xs={12}>
                                            <Button
                                                variant='outlined'
                                                size="large"
                                                fullWidth
                                                className={signinStyle.backToLoginPageBtnForSmallScreen}
                                                onClick={() => setFlagForgotPwdClick(false)}
                                            >
                                                Back to login Page
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Divider
                                    sx={{
                                        opacity: 0.8,
                                        borderColor: "#E5E7EB",
                                        height: "1px",
                                        width: "100%",
                                        margin: "0 auto"
                                    }}
                                />
                                <Grid item xs={12}>
                                    <Grid container gap={1}>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerHeadingTextForSmallScreen}>
                                                Disclaimer
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerTextForSmallScreen}>
                                                This presentation is a general discussion of the topic presented, based on our current understanding of applicable tax laws, regulations, and rulings.
                                                This information and presentation in no way should be interpreted as investment or tax advice. In actual practice, the transactions discussed may be more complex and will require the attention and expertise of professional tax and legal advisors.
                                                In order to comply with certain U.S. Treasury regulations, please be advised of the following: unless expressly stated otherwise, any reference to U.S. Federal tax information contained in these materials,
                                                including any attachments, is not intended or written to be used, and cannot be used, by any person for the purpose of avoiding any penalties that may be imposed
                                                by the Internal Revenue Service. Values and benefits are not guaranteed unless stated as guaranteed on illustrations.
                                                Examples are used to illustrate the potential benefits of indexed universal life insurance / IRS SEC. 7702 & 72(e).
                                                Portions of this presentation are based on NAIC compliant life insurance illustrations which must be present during the presentation and fully reviewed
                                                for additional disclosures.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <Typography className={signinStyle.disclaimerTextForSmallScreen}>
                                                        © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography component="div" className={signinStyle.disclaimerTextForSmallScreen}>
                                                        <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#fff', textDecoration: 'underline', fontSize: 'inherit' }}>
                                                            Terms and Conditions
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#fff', textDecoration: 'underline', fontSize: 'inherit' }}>
                                                            Privacy Policy
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/user-agreement" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#fff', textDecoration: 'underline', fontSize: 'inherit' }}>
                                                            User Agreement
                                                        </Link>
                                                    </Typography>
                                                </Grid>

                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                    :
                    <>
                        <Grid item xs={12} className={signinStyle.welcomeContainer}>
                            <Grid container gap={3}>
                                <Grid item xs={12}>
                                    <Grid container alignItems="center" className={signinStyle.welcomeTextContainer}>
                                        <Grid item xs={12}>
                                            <Typography component={'span'} className={signinStyle.welcomeTextForSmallScreen}>
                                                Welcome Back
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.rightSideSubTextForSmallScreen}>
                                                Sign in to continue your retirement planning journey
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {apiError && (
                                    <Grid item xs={12}>
                                        <Box className={signinStyle.apiErrorBox}>
                                            <Box className={signinStyle.apiErrorIconWrapper}>
                                                <ErrorOutlineIcon fontSize="small" />
                                            </Box>
                                            <Typography className={signinStyle.apiErrorText}>
                                                {apiError}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Grid container gap={"18px"}>
                                        <Grid item xs={12}>
                                            <TextField
                                                required
                                                value={signinFormDetails.email}
                                                variant="outlined"
                                                placeholder='Email Address'
                                                fullWidth
                                                className={signinStyle.signinTextfield}
                                                onChange={handleEmailChange}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <EmailIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            // onBlur={() => handleBlur('email')}
                                            />
                                            {touched.email && errors.email && (
                                                <span style={{ color: '#810505', marginLeft: '7px', fontSize: "14px" }}>
                                                    {errors.email}
                                                </span>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                required
                                                value={signinFormDetails.password}
                                                variant="outlined"
                                                placeholder='Password'
                                                type={showPassword ? 'text' : 'password'}
                                                fullWidth
                                                className={signinStyle.signinTextfield}
                                                onChange={handlePasswordChange}
                                                // onBlur={() => handleBlur('password')}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <HttpsIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={handleClickShowPassword}
                                                                edge="end"
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {touched.password && errors.password && (
                                                <span style={{ color: '#810505', marginLeft: '7px', fontSize: "14px" }}>
                                                    {errors.password}
                                                </span>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Grid item>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={signinFormDetails.remember_me_flag}
                                                        onChange={(event) => {
                                                            setSigninFormDetails({
                                                                ...signinFormDetails,
                                                                remember_me_flag: event.target.checked,
                                                            });
                                                        }}
                                                    />
                                                }
                                                label="Remember Me"
                                                className={signinStyle.signinCheckboxLabelForSmallScreen}
                                            />
                                        </Grid>
                                        <Grid item justifyContent={"flex-end"}>
                                            {/* <span><Link href='/forgot-password' sx={{ color: "black", fontSize: "16px", textDecoration: "none" }}>Forgot Password?</Link></span> */}
                                            <span className={signinStyle.forgotPwdTextForSmallScreen} onClick={() => setFlagForgotPwdClick(true)}>Forgot Password?</span>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Grid container gap={2} justifyContent={"center"}>
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant='contained'
                                                        size='large'
                                                        fullWidth
                                                        className={signinStyle.signinBtn}
                                                        onClick={handleSignIn}
                                                    >
                                                        Sign in
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} textAlign={"center"}>
                                                    <span className={signinStyle.noAccTxt}>Don't have an account?
                                                        <span onClick={() => { navigate('/register') }} style={{ color: "#A3C9FF", cursor: "pointer", textDecoration: "underline" }}> Register</span>
                                                    </span>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Divider
                                            className={signinStyle.signindivider}
                                            style={{ width: '100%', margin: '40px 0px' }}
                                        >
                                            <span className={signinStyle.signinDividerOrLabel}>Or</span>
                                        </Divider>
                                        <Grid item xs={12}>
                                            <Button
                                                variant='contained'
                                                size="large"
                                                fullWidth
                                                startIcon={<img src={google} style={{ width: '25px', height: 'auto' }} />}
                                                className={signinStyle.signinWithGooglebtn}
                                                onClick={handleGoogleLogin}
                                            >
                                                Sign in with Google
                                            </Button>
                                        </Grid>
                                        <GoogleSignInNoticeBox text={googleSignInNotice} />
                                    </Grid>
                                </Grid>
                                <Divider
                                    sx={{
                                        opacity: 0.8,
                                        borderColor: "#E5E7EB",
                                        height: "1px",
                                        width: "100%",
                                        margin: "0 auto"
                                    }}
                                />
                                <Grid item xs={12}>
                                    <Grid container gap={1}>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerHeadingTextForSmallScreen}>
                                                Disclaimer
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerTextForSmallScreen}>
                                                This presentation is a general discussion of the topic presented, based on our current understanding of applicable tax laws, regulations, and rulings.
                                                This information and presentation in no way should be interpreted as investment or tax advice. In actual practice, the transactions discussed may be more complex and will require the attention and expertise of professional tax and legal advisors.
                                                In order to comply with certain U.S. Treasury regulations, please be advised of the following: unless expressly stated otherwise, any reference to U.S. Federal tax information contained in these materials,
                                                including any attachments, is not intended or written to be used, and cannot be used, by any person for the purpose of avoiding any penalties that may be imposed
                                                by the Internal Revenue Service. Values and benefits are not guaranteed unless stated as guaranteed on illustrations.
                                                Examples are used to illustrate the potential benefits of indexed universal life insurance / IRS SEC. 7702 & 72(e).
                                                Portions of this presentation are based on NAIC compliant life insurance illustrations which must be present during the presentation and fully reviewed
                                                for additional disclosures.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <Typography className={signinStyle.disclaimerTextForSmallScreen}>
                                                        © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography component="div" className={signinStyle.disclaimerTextForSmallScreen}>
                                                        <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#fff', textDecoration: 'underline', fontSize: 'inherit' }}>
                                                            Terms and Conditions
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#fff', textDecoration: 'underline', fontSize: 'inherit' }}>
                                                            Privacy Policy
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/user-agreement" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#fff', textDecoration: 'underline', fontSize: 'inherit' }}>
                                                            User Agreement
                                                        </Link>
                                                    </Typography>
                                                </Grid>

                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                }
            </Grid>
        </Grid>
    );

    const renderDesktopLayout = () => (
        <>
            <Grid item xs={6} className={signinStyle.leftSection}>
                <Grid container gap={4} padding={"100px"}>
                    <Grid item xs={12}>
                        {/* {
                                            companyDetails?.company_full_logo &&
                                            <Grid item xs={12} className={signinStyle.logoContainer}>
                                                <img style={{ height: "80px" }} src={companyDetails?.company_full_logo} alt={BRAND_NAME} />
                                            </Grid>
                                        } */}
                        <Box sx={{ position: 'absolute', top: 0, left: 0, zIndex: 2, padding: "50px 100px" }}>
                            <Box
                                component="img"
                                alt="Logo"
                                src={newRBlogo}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container className={signinStyle.pointsContainer}>
                            <Grid item xs={12}>
                                <Typography component='span' className={signinStyle.title}>
                                    Turn Complex Retirement Planning into Simple Visual Storytelling
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography className={signinStyle.subText}>
                                    A powerful platform combining interactive financial calculators with innovative products for agents and consumers.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} className={signinStyle.pointsWrapper}>
                                <Grid container direction="column" gap={2}>
                                    {points.map((item, index) => (
                                        <Grid item xs={12} key={index}>
                                            <Grid container className={signinStyle.pointItem}>
                                                <Grid item className={signinStyle.iconWrapper}>
                                                    <item.img sx={{ color: "#fff" }} />
                                                </Grid>
                                                <Grid item className={signinStyle.textWrapper}>
                                                    {item.text}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={6} className={signinStyle.rightSection} alignItems={"flex-start"}>
                <Grid container className={signinStyle.rightMainContainer}>
                    {flagForgotPwdClick ?
                        <Grid item xs={8} className={signinStyle.welcomeContainer}>
                            <Grid container gap={4}>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Grid container alignItems="center" className={signinStyle.welcomeTextContainer}>
                                                <Typography component={'span'} className={signinStyle.welcomeText}>
                                                    Forgot Password?
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.subTextFwdPass}>
                                                We've sent you a password reset link
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <GlobalTextfield
                                        // required
                                        variant="outlined"
                                        placeholder='Email Address'
                                        fullWidth
                                        stylingClassName={signinStyle.signinTextfield}
                                        value={forgetPwdEmail}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;
                                            setForgetPwdEmail(inputValue);

                                            if (inputValue && emailRegex.test(inputValue)) {
                                                setForgotPwdError("");
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {forgotPwdError && (
                                        <span style={{ color: '#810505', marginLeft: '7px', fontSize: "14px" }}>
                                            {forgotPwdError}
                                        </span>
                                    )}
                                    <Typography className={signinStyle.subTextFwdPassReset}>
                                        Click the link in the email to reset your password. If you don't see the email, check your spam folder.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Button
                                                variant='contained'
                                                size='large'
                                                fullWidth
                                                className={signinStyle.signinBtn}
                                                onClick={() => handleForgetPassword()}
                                            >
                                                Get Reset Link
                                            </Button>
                                        </Grid>
                                        <Divider
                                            className={signinStyle.signindivider}
                                            style={{ width: '100%', margin: '40px 0px' }}
                                        >
                                            <span className={signinStyle.signinDividerOrLabelMuted}>Or</span>
                                        </Divider>
                                        <Grid item xs={12}>
                                            <Button
                                                variant='outlined'
                                                size="large"
                                                fullWidth
                                                className={signinStyle.backToLoginPageBtn}
                                                onClick={() => setFlagForgotPwdClick(false)}
                                            >
                                                Back to login Page
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Divider
                                    sx={{
                                        opacity: 0.8,
                                        borderColor: "#E5E7EB",
                                        height: "1px",
                                        width: "100%",
                                        margin: "0 auto"
                                    }}
                                />
                                <Grid item xs={12}>
                                    <Grid container gap={1}>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerHeadingText}>
                                                Disclaimer
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerText}>
                                                This presentation is a general discussion of the topic presented, based on our current understanding of applicable tax laws, regulations, and rulings.
                                                This information and presentation in no way should be interpreted as investment or tax advice. In actual practice, the transactions discussed may be more complex and will require the attention and expertise of professional tax and legal advisors.
                                                In order to comply with certain U.S. Treasury regulations, please be advised of the following: unless expressly stated otherwise, any reference to U.S. Federal tax information contained in these materials,
                                                including any attachments, is not intended or written to be used, and cannot be used, by any person for the purpose of avoiding any penalties that may be imposed
                                                by the Internal Revenue Service. Values and benefits are not guaranteed unless stated as guaranteed on illustrations.
                                                Examples are used to illustrate the potential benefits of indexed universal life insurance / IRS SEC. 7702 & 72(e).
                                                Portions of this presentation are based on NAIC compliant life insurance illustrations which must be present during the presentation and fully reviewed
                                                for additional disclosures.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <Typography className={signinStyle.disclaimerText}>
                                                        © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography component="div" className={signinStyle.disclaimerText}>
                                                        <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#3B78CE', fontSize: 'inherit' }}>
                                                            Terms and Conditions
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#3B78CE', fontSize: 'inherit' }}>
                                                            Privacy Policy
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/user-agreement" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#3B78CE', fontSize: 'inherit' }}>
                                                            User Agreement
                                                        </Link>
                                                    </Typography>
                                                </Grid>

                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        :
                        <Grid item xs={8} className={signinStyle.welcomeContainer}>
                            <Grid container gap={3}>
                                <Grid item xs={12}>
                                    <Grid container alignItems="center" className={signinStyle.welcomeTextContainer}>
                                        <Grid item xs={12}>
                                            <Typography component={'span'} className={signinStyle.welcomeText}>
                                                Welcome Back
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.rightSideSubText}>
                                                Sign in to continue your retirement planning journey
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {apiError && (
                                    <Grid item xs={12}>
                                        <Box className={signinStyle.apiErrorBox}>
                                            <Box className={signinStyle.apiErrorIconWrapper}>
                                                <ErrorOutlineIcon fontSize="small" />
                                            </Box>
                                            <Typography className={signinStyle.apiErrorText}>
                                                {apiError}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Grid container gap={"18px"}>
                                        <Grid item xs={12}>
                                            <TextField
                                                required
                                                value={signinFormDetails.email}
                                                variant="outlined"
                                                placeholder='Email Address'
                                                fullWidth
                                                className={signinStyle.signinTextfield}
                                                onChange={handleEmailChange}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <EmailIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            // onBlur={() => handleBlur('email')}
                                            />
                                            {touched.email && errors.email && (
                                                <span style={{ color: '#810505', marginLeft: '7px', fontSize: "14px" }}>
                                                    {errors.email}
                                                </span>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                required
                                                value={signinFormDetails.password}
                                                variant="outlined"
                                                placeholder='Password'
                                                type={showPassword ? 'text' : 'password'}
                                                fullWidth
                                                className={signinStyle.signinTextfield}
                                                onChange={handlePasswordChange}
                                                // onBlur={() => handleBlur('password')}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <HttpsIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={handleClickShowPassword}
                                                                edge="end"
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {touched.password && errors.password && (
                                                <span style={{ color: '#810505', marginLeft: '7px', fontSize: "14px" }}>
                                                    {errors.password}
                                                </span>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Grid item>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={signinFormDetails.remember_me_flag}
                                                        onChange={(event) => {
                                                            setSigninFormDetails({
                                                                ...signinFormDetails,
                                                                remember_me_flag: event.target.checked,
                                                            });
                                                        }}
                                                    />
                                                }
                                                label="Remember Me"
                                                className={signinStyle.signinCheckboxLabel}
                                            />
                                        </Grid>
                                        <Grid item justifyContent={"flex-end"}>
                                            {/* <span><Link href='/forgot-password' sx={{ color: "black", fontSize: "16px", textDecoration: "none" }}>Forgot Password?</Link></span> */}
                                            <span className={signinStyle.forgotPwdText} onClick={() => setFlagForgotPwdClick(true)}>Forgot Password?</span>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Grid container gap={2} justifyContent={"center"}>
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant='contained'
                                                        size='large'
                                                        fullWidth
                                                        className={signinStyle.signinBtn}
                                                        onClick={handleSignIn}
                                                    >
                                                        Sign in
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} textAlign={"center"}>
                                                    <span className={signinStyle.noAccTxt}>Don't have an account?
                                                        <span onClick={() => { navigate('/register') }} style={{ color: "#3B78CE", cursor: "pointer", textDecoration: "underline" }}> Register</span>
                                                    </span>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Divider
                                            className={signinStyle.signindivider}
                                            style={{ width: '100%', margin: '40px 0px' }}
                                        >
                                            <span style={{ color: "#6A7282 !important" }}>Or</span>
                                        </Divider>
                                        <Grid item xs={12}>
                                            <Button
                                                variant='contained'
                                                size="large"
                                                fullWidth
                                                startIcon={<img src={google} style={{ width: '25px', height: 'auto' }} />}
                                                className={signinStyle.signinWithGooglebtn}
                                                onClick={handleGoogleLogin}
                                            >
                                                Sign in with Google
                                            </Button>
                                        </Grid>
                                        <GoogleSignInNoticeBox text={googleSignInNotice} />
                                    </Grid>
                                </Grid>
                                <Divider
                                    sx={{
                                        opacity: 0.8,
                                        borderColor: "#E5E7EB",
                                        height: "1px",
                                        width: "100%",
                                        margin: "0 auto"
                                    }}
                                />
                                <Grid item xs={12}>
                                    <Grid container gap={1}>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerHeadingText}>
                                                Disclaimer
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={signinStyle.disclaimerText}>
                                                This presentation is a general discussion of the topic presented, based on our current understanding of applicable tax laws, regulations, and rulings.
                                                This information and presentation in no way should be interpreted as investment or tax advice. In actual practice, the transactions discussed may be more complex and will require the attention and expertise of professional tax and legal advisors.
                                                In order to comply with certain U.S. Treasury regulations, please be advised of the following: unless expressly stated otherwise, any reference to U.S. Federal tax information contained in these materials,
                                                including any attachments, is not intended or written to be used, and cannot be used, by any person for the purpose of avoiding any penalties that may be imposed
                                                by the Internal Revenue Service. Values and benefits are not guaranteed unless stated as guaranteed on illustrations.
                                                Examples are used to illustrate the potential benefits of indexed universal life insurance / IRS SEC. 7702 & 72(e).
                                                Portions of this presentation are based on NAIC compliant life insurance illustrations which must be present during the presentation and fully reviewed
                                                for additional disclosures.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <Typography className={signinStyle.disclaimerText}>
                                                        © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography component="div" className={signinStyle.disclaimerText}>
                                                        <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#3B78CE', fontSize: 'inherit' }}>
                                                            Terms and Conditions
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#3B78CE', fontSize: 'inherit' }}>
                                                            Privacy Policy
                                                        </Link>
                                                        {' | '}
                                                        <Link href="/user-agreement" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#3B78CE', fontSize: 'inherit' }}>
                                                            User Agreement
                                                        </Link>
                                                    </Typography>
                                                </Grid>

                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>
        </>
    );

    return (
        <Grid
            container
            direction="column"
            className={signinStyle.signinMainContainer}
            sx={{
                ...(isLessThan1024 && {
                    background: 'linear-gradient(135deg, rgba(4, 56, 93, 0.9) 0%, rgba(4, 56, 93, 0.8) 50%, rgba(59, 120, 206, 0.7) 100%)',
                }),
            }}
        >
            <Grid item xs={12} className={signinStyle.signinMainContainerItem}>
                <Grid container wrap="nowrap" alignItems="stretch" className={signinStyle.signinMainContainerRow}>
                    {isLessThan1024 ? renderMobileLayout() : renderDesktopLayout()}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Signin;